import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Typography, Button, Paper, CircularProgress,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useParams, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import { fetchProject } from "../../api/memberApi";
import { useNotification } from "../../providers/NotificationProvider";

const formatDate = (iso) => {
  if (!iso) return "—";
  return iso.substring(0, 10).replace(/-/g, "/");
};

function DetailRow({ label, value }) {
  return (
    <Box
      sx={{
        display: "flex",
        py: 1.5,
        borderBottom: "1px solid",
        borderColor: "divider",
      }}
    >
      <Typography
        variant="body2"
        sx={{ width: 200, flexShrink: 0, color: "text.secondary", fontWeight: 500 }}
      >
        {label}
      </Typography>
      <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>{value || "—"}</Typography>
    </Box>
  );
}

export default function ProjectGoalDetailPage() {
  const { businessDomainId, businessUnitId, projectId, goalId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });
  const basePath = `/strategy/${businessDomainId}/business-units/${businessUnitId}/projects/${projectId}`;

  const [goal, setGoal] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const d = await fetchProject(projectId);
      const found = (d.goals || [])[Number(goalId)];
      setGoal(found || null);
    } catch (e) {
      notify.apiError(e);
    } finally {
      setLoading(false);
    }
  }, [projectId, goalId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadData(); }, [loadData]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!goal) return null;

  const indicator = goal.indicator || {};

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", mb: 3, gap: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`${basePath}/goals`)}
          variant="text"
        >
          {t("common.back")}
        </Button>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          {t("project.goal.detail.title")}
        </Typography>
      </Box>

      <Paper variant="outlined" sx={{ p: 3 }}>
        {/* 指標名 */}
        <DetailRow label={t("project.goal.indicator")} value={indicator.indicatorName} />

        {/* 指標詳細（単位・測定方法・評価基準・説明） */}
        {indicator.unit && (
          <DetailRow label={t("appKpi.unit")} value={indicator.unit} />
        )}
        {indicator.measuringMethod && (
          <DetailRow label={t("appKpi.measuringMethod")} value={indicator.measuringMethod} />
        )}
        {indicator.evaluationStandard && (
          <DetailRow label={t("appKpi.evaluationStandard")} value={indicator.evaluationStandard} />
        )}
        {indicator.description && (
          <DetailRow label={t("appKpi.description")} value={indicator.description} />
        )}

        {/* 計画値 */}
        <DetailRow
          label={t("project.goal.plannedValue")}
          value={
            goal.plannedValue != null && goal.plannedValue !== ""
              ? `${goal.plannedValue}${indicator.unit ? `（${indicator.unit}）` : ""}`
              : null
          }
        />

        {/* 計画日・期限 */}
        <DetailRow label={t("project.goal.plannedDate")} value={formatDate(goal.plannedDate)} />
        <DetailRow label={t("project.goal.deadline")} value={formatDate(goal.deadline)} />
      </Paper>
    </Box>
  );
}
